import { generateBufferLinesAsync } from "@nova/calculate-buffers"
import { getApiGatewayDomainForRegion } from "@nova/configuration"
import { GeometryType } from "@nova/constants"
import { Coordinate, Icon, Line, Perimeter } from "@nova/protobuf"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"

const DEFAULT_COLOR = "#E51A1A"

type CalTopoStyle = { color: string; opacity: number }

type CalTopoGeometry =
    | { type: "Polygon"; coordinates: [number, number][][] }
    | { type: "LineString"; coordinates: [number, number][] }

const caltopoColor = (encoded: number | undefined) => {
    if (!encoded) return { color: DEFAULT_COLOR, opacity: 1 }

    const hex = decodeNumberToColor(encoded)
    return { color: hex.slice(0, 7), opacity: parseInt(hex.slice(7, 9), 16) / 255 }
}

const ring = (coordinates: Coordinate[]) =>
    coordinates.map(({ longitude, latitude }) => [longitude, latitude] as [number, number])

const shape = (
    title: string,
    geometry: CalTopoGeometry,
    stroke: CalTopoStyle,
    width: number | undefined,
    fill?: CalTopoStyle
) => ({
    type: "Feature",
    geometry,
    properties: {
        class: "Shape",
        title,
        description: "",
        stroke: stroke.color,
        "stroke-width": width ?? 2,
        "stroke-opacity": stroke.opacity,
        ...(geometry.type === "LineString" ? { pattern: "solid" } : {}),
        ...(fill ? { fill: fill.color, "fill-opacity": fill.opacity } : {}),
    },
})

export const perimeterFeatures = async (perimeter: Perimeter) => {
    const stroke = caltopoColor(perimeter.outline)

    const polygons = perimeter.polygons.map((polygon) =>
        shape(
            perimeter.name,
            { type: "Polygon", coordinates: [ring(polygon.coordinates)] },
            stroke,
            perimeter.width
        )
    )

    if (!perimeter.buffers) return polygons

    const buffers = (await generateBufferLinesAsync(perimeter.polygons, perimeter.buffers)).flat()

    return [
        ...polygons,
        ...buffers.map((buffer) =>
            shape(
                perimeter.name,
                { type: "Polygon", coordinates: [ring(buffer.coordinates)] },
                caltopoColor(buffer.color),
                buffer.width
            )
        ),
    ]
}

export const lineFeatures = (line: Line) => {
    const stroke = caltopoColor(line.outline)
    const coordinates = ring(line.coordinates)

    return line.geometryType === GeometryType.Polygon
        ? [
              shape(
                  line.name,
                  { type: "Polygon", coordinates: [coordinates] },
                  stroke,
                  line.width,
                  line.fill ? caltopoColor(line.fill) : undefined
              ),
          ]
        : [shape(line.name, { type: "LineString", coordinates }, stroke, line.width)]
}

const iconUrl = (icon: Icon) => {
    const url = new URL(`${getApiGatewayDomainForRegion()}/public/icon`)
    url.searchParams.set("url", icon.url)
    if (icon.color) url.searchParams.set("color", String(icon.color))
    return url.toString()
}

export const iconFeatures = (icon: Icon) => [
    {
        type: "Feature",
        geometry: { type: "Point", coordinates: [icon.longitude, icon.latitude] },
        properties: {
            class: "Marker",
            title: icon.name,
            description: "",
            "marker-color": caltopoColor(icon.color).color,
            "marker-symbol": iconUrl(icon),
            "marker-size": 1,
        },
    },
]
